import { useLang } from 'hooks/lang';

import { loaders, recruitmentStatusConfig, User } from 'entities';

import { Button, Card, Column, IconButton, Label, Row, Table } from 'components/Blocks';
import { AssignRecruitmentIconButton } from 'pages/Recruitment/components';
import { UserAvatar } from 'components/UserAvatar';
import { ExpandIcon, LocationMapIcon } from 'components/Icons';

import { usePendingActions } from 'providers/PendingActions';
import { useProject } from 'providers/Project';

import { TwoColumnWrapper } from '../components';
import { UserRecruitments } from './UserRecruitments';

import { OnRecruitment, useRecruitmentAssign, withRecruitmentAssignContext } from './context';

import { listRemove } from 'utils/array';
import { truncateText } from 'utils/text';
import { getCountryLabel } from 'utils/countries';
import { DEFAULT_NO_OWNER_USER } from 'utils/constants';

function RecruitmentAssignInner() {
  const { t } = useLang();

  const { projects } = useProject();
  const { decreaseCount } = usePendingActions();

  const { openRecruitment } = useRecruitmentAssign();

  const recruitments = loaders.projects.listUnassignedRecruitments.use({
    defaultValue: [],
  });

  const removeRecruitment = ({ id }: { id: string }): void => {
    recruitments.overwrite((old) => listRemove(old, id));

    decreaseCount('UNASSIGNED_RECRUITMENTS');
  };

  const onRecruitment: OnRecruitment = (recruitment): void => {
    if (recruitment.status === recruitmentStatusConfig.unassigned.code) return;

    removeRecruitment(recruitment);
  };

  return (
    <TwoColumnWrapper>
      <Card>
        <Table
          items={recruitments.data}
          loading={recruitments.loading}
          uniqueKey="id"
          spacing="medium"
          columns={[
            {
              label: 'Sample ID',
              property: 'sampleId',
              filter: true,
            },

            {
              label: t('project')!,
              search: true,
              sort: true,
              className: 'text-center',

              render: ({ project }) => (
                <Button
                  tooltipContent={projects[project]?.name ?? '#REF'}
                  tooltipDisabled={projects[project]?.name?.length < 35}
                  variant="ghost"
                  to="projectProfile"
                  params={{ projectId: project }}
                >
                  <UserAvatar user={projects[project].owner} size={28} />

                  {truncateText(projects[project]?.name ?? '#REF', 35)}
                </Button>
              ),
            },
            {
              label: t('client')!,
              filter: true,
              getTextValue: ({ project }) => projects[project].clientName,

              render: ({ project }) => (
                <Column gap="1">
                  <Label>{projects[project].clientName}</Label>
                  <Label icon={LocationMapIcon}>{projects[project].clientOrigin}</Label>
                </Column>
              ),
            },
            {
              label: t('countries')!,

              render: ({ project }) =>
                projects[project].countries.map((code) => getCountryLabel(code)).join(', '),
            },

            {
              label: t('requested-by'),
              filter: true,
              className: 'text-center',
              getTextValue: ({ requestedBy }) => requestedBy.name,
              render: ({ requestedBy }) => (
                <Row justifyContent="center">
                  <UserAvatar user={requestedBy} size={28} name />
                </Row>
              ),
            },

            {
              label: t('country')!,
              property: 'country',
              filter: true,
              sort: true,
              render: ({ country }) => getCountryLabel(country),
            },
            {
              className: 'w-20',

              render: (recruitment) => (
                <Row justifyContent="flex-end" gap="1">
                  <AssignRecruitmentIconButton
                    recruitment={{ ...recruitment, owner: DEFAULT_NO_OWNER_USER as User }}
                    updateRecruitment={removeRecruitment}
                  />

                  <IconButton onClick={() => openRecruitment(recruitment.id, onRecruitment)}>
                    <ExpandIcon />
                  </IconButton>
                </Row>
              ),
            },
          ]}
        />
      </Card>

      <UserRecruitments />
    </TwoColumnWrapper>
  );
}

export const RecruitmentAssign = withRecruitmentAssignContext(RecruitmentAssignInner);
